import cuid from "cuid";
import { get, head } from "lodash";
import moment from "moment";
import format from "string-template";
import { CONTACT_POINT_SYSTEM, DATE_FORMAT, REGEX_EXPS } from "utils/constants";
import { applyAcorisRiaOlderThanOneYearTest } from "screens/Private/Broker/Projects/Detail/Templates/Comparison/Acoris/utils/riaBlock";
import {
  isAcorisZipCode,
  isForeignResidentZipCode,
} from "screens/Private/Broker/Projects/Detail/Templates/Comparison/Acoris/utils/zipCode";
import { isAcorisAdultBirthDateAllowed } from "utils/helpers/date";
import {
  getPhonePrefixFromYupContext,
  isValidLocalPhoneNumber,
} from "utils/helpers/phone";
import { normalizeTextField } from "utils/helpers/validations";
import * as yup from "yup";

const validator = (
  messages,
  { fetchZipCodeCities, setCity, deltaRia, deltaStart },
) => {
  const requiredString = yup
    .string()
    .typeError(messages.REQUIRED)
    .required(messages.REQUIRED);
  const requiredName = yup
    .string()
    .transform(normalizeTextField)
    .typeError(messages.REQUIRED)
    .required(messages.REQUIRED)
    .matches(REGEX_EXPS.name, messages.INVALID_NAME);
  const childValidationSchema = yup.object().shape({
    identity: yup.object().shape({
      user: yup.object().shape({
        lastname: requiredName,
        firstname: requiredName,
        birthDate: yup
          .date()
          .typeError(messages.REQUIRED)
          .max(moment(), messages.MIN_TODAY)
          .min(
            moment().subtract(27, "year").startOf("year"),
            messages.ACORIS.CHILD_AGE,
          )
          .required(messages.REQUIRED),
        gender: requiredString,
      }),
      socialRegime: requiredString,
    }),
  });
  return yup.object().shape({
    contract: yup.mixed().when("locked", {
      is: (locked) => locked,
      then: yup.object().shape({
        issuanceDate: yup
          .date()
          .typeError(messages.REQUIRED)
          .required(messages.REQUIRED),
      }),
      otherwise: yup.mixed().when("retroactive", {
        is: true,
        then: yup.object().shape({
          issuanceDate: yup
            .date()
            .typeError(messages.REQUIRED)
            .min(
              moment().startOf("month"),
              format(messages.MIN_DATE, {
                date: moment().startOf("month").format(DATE_FORMAT),
              }),
            )
            .required(messages.REQUIRED),
        }),
        otherwise: yup.mixed().when("ria", {
          is: (ria) => ria,
          then: yup.object().shape({
            issuanceDate: yup
              .date()
              .typeError(messages.REQUIRED)
              .min(
                moment().startOf("day").add(deltaRia, "days"),
                format(messages.MIN_DATE, {
                  date: moment()
                    .startOf("day")
                    .add(deltaRia, "days")
                    .format(DATE_FORMAT),
                }),
              )
              .required(messages.REQUIRED),
          }),
          otherwise: yup.object().shape({
            issuanceDate: yup
              .date()
              .typeError(messages.REQUIRED)
              .min(
                moment().startOf("day").add(deltaStart, "days"),
                format(messages.MIN_DATE, {
                  date: moment()
                    .startOf("day")
                    .add(deltaStart, "days")
                    .format(DATE_FORMAT),
                }),
              )
              .required(messages.REQUIRED),
          }),
        }),
      }),
    }),
    hasOtherInsurer: yup.boolean().nullable(),
    contractOlderThanOneYear: yup.boolean().when("hasOtherInsurer", {
      is: true,
      then: applyAcorisRiaOlderThanOneYearTest(
        yup.boolean().typeError(messages.REQUIRED).required(messages.REQUIRED),
        messages.ACORIS && messages.ACORIS.RIA_UNDER_ONE_YEAR,
      ),
      otherwise: yup.boolean().nullable(),
    }),
    terminationRequest: yup.mixed().when("contractOlderThanOneYear", {
      is: true,
      then: yup.object().shape({
        dueDate: yup
          .date()
          .typeError(messages.REQUIRED)
          .max(
            moment().subtract(1, "year").endOf("day"),
            format(messages.MAX_DATE, {
              date: moment().subtract(1, "year").format(DATE_FORMAT),
            }),
          )
          .required(messages.REQUIRED),
        contractNumber: requiredString,
        brokerageMandate: yup.object().shape({
          currentCompany: requiredString,
        }),
      }),
      otherwise: yup.mixed().nullable(),
    }),
    contact: yup.object().shape({
      fields: yup.object().shape({
        profile: requiredString,
        isClient: yup.boolean().nullable(),
      }),
      user: yup.object().shape({
        gender: requiredString,
        lastname: requiredName,
        firstname: requiredName,
        birthDate: yup
          .date()
          .typeError(messages.REQUIRED)
          .test("acoris-adult-birth-date", messages.AGE_16, (value) => {
            if (!value) return true;
            return isAcorisAdultBirthDateAllowed(value);
          })
          .required(messages.REQUIRED),
      }),
      socialRegime: requiredString,
    }),
    zipCode: yup.object().shape({
      value: yup
        .string()
        .typeError(messages.REQUIRED)
        .test("isValid", messages.INVALID_VALUE, async function (value) {
          if (!isAcorisZipCode(value)) return false;
          if (isForeignResidentZipCode(value)) {
            setCity({
              id: cuid(),
              system: CONTACT_POINT_SYSTEM.CITY,
              value: null,
            });
            return true;
          }
          const response = await fetchZipCodeCities(value);
          setCity(
            response.length === 1
              ? {
                  id: cuid(),
                  system: CONTACT_POINT_SYSTEM.CITY,
                  value: get(head(response), "value"),
                }
              : {
                  id: cuid(),
                  system: CONTACT_POINT_SYSTEM.CITY,
                  value: null,
                },
          );
          return response.length;
        }),
    }),
    phoneNumber: yup.object().shape({
      value: yup
        .string()
        .typeError(messages.REQUIRED)
        .required(messages.REQUIRED)
        .test("isValid", messages.INVALID_PHONE, function (value) {
          const prefix = getPhonePrefixFromYupContext(this);
          return isValidLocalPhoneNumber(
            String(value || "").replace(/\s+/g, ""),
            prefix,
          );
        }),
    }),
    children: yup.array().of(childValidationSchema).max(12),
    hasSpouse: yup.boolean().nullable(),
    spouse: yup.object().when("hasSpouse", {
      is: (hasSpouse) => hasSpouse,
      then: yup.object().shape({
        identity: yup.object().shape({
          user: yup.object().shape({
            lastname: requiredName,
            firstname: requiredName,
            birthDate: yup
              .date()
              .typeError(messages.REQUIRED)
              .test(
                "acoris-spouse-birth-date",
                messages.ACORIS.SPOUSE_AGE,
                (value) => {
                  if (!value) return true;
                  return isAcorisAdultBirthDateAllowed(value);
                },
              )
              .required(messages.REQUIRED),
            gender: requiredString,
          }),
          socialRegime: requiredString,
        }),
      }),
    }),
  });
};

export default validator;
